import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ErrorMessagePipe } from '@app/shared/error-message.pipe';
import { CircleAlert, LucideAngularModule, Plus, Trash2 } from 'lucide-angular';
import { NgxMaskDirective } from 'ngx-mask';
import { NgClass } from '@angular/common';
import { Destroyer } from '@app/shared/abstract/destroyer';
import { takeUntil } from 'rxjs';

@Component({
  selector: 'app-array-input',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, ErrorMessagePipe, LucideAngularModule, NgxMaskDirective, NgClass],
  templateUrl: './array-input.component.html',
})
export class ArrayInputComponent extends Destroyer implements OnInit {
  protected readonly Object = Object;
  protected readonly Validators = Validators;
  protected readonly CircleAlert = CircleAlert;
  protected readonly Plus = Plus;
  protected readonly Trash2 = Trash2;

  @Input() label: string = '';
  @Input() name: string = '';
  @Input() placeholder: string = '';
  @Input() type: string = 'text';
  @Input() formArray!: FormArray;
  @Input() mask: string = '';
  @Input() thousandSeparator: string = '';
  @Input() maxEntries: number = Infinity;
  @Input() minEntries: number = 0;
  @Input() static: boolean = false;
  @Input() addButtonText: string = 'Add';

  constructor(private fb: FormBuilder) {
    super();
  }

  ngOnInit(): void {
    // Initialize with minEntries if the array is empty
    if (this.formArray.controls.length < this.minEntries) {
      const controlsToAdd = this.minEntries - this.formArray.controls.length;
      for (let i = 0; i < controlsToAdd; i++) {
        this.addControl();
      }
    }

    // Subscribe to value changes to maintain min entries
    this.formArray.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.formArray.controls.length < this.minEntries) {
        const controlsToAdd = this.minEntries - this.formArray.controls.length;
        for (let i = 0; i < controlsToAdd; i++) {
          this.addControl();
        }
      }
    });
  }

  addControl(): void {
    if (this.formArray.controls.length < this.maxEntries) {
      this.formArray.push(new FormControl(''));
    }
  }

  removeControl(index: number): void {
    if (this.formArray.controls.length > this.minEntries) {
      this.formArray.removeAt(index);
      this.formArray.markAsDirty();
    }
  }

  canAddMore(): boolean {
    return !this.static && this.formArray.controls.length < this.maxEntries;
  }

  canRemove(index: number): boolean {
    return !this.static && this.formArray.controls.length > this.minEntries;
  }
}
